import {
  Activity,
  Bot,
  Brain,
  Layers,
  Loader2,
  Sparkles,
  Target,
  Wand2,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/api/client";
import { toastApiError } from "@/api/errors";
import { startOidcLogin } from "@/auth/oidcRedirect";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { env } from "@/lib/env";

interface DevUser {
  email: string;
  label: string;
}

const ROTATING_WORDS = [
  "agentes",
  "ferramentas",
  "conhecimento",
  "fluxos",
  "memória",
  "respostas",
];
const MARQUEE_KEYWORDS = [
  "Agentes",
  "Conhecimento",
  "MCPs",
  "Sandbox",
  "Avaliações",
  "Times",
  "PII",
  "Observabilidade",
  "Streaming",
  "Tools",
  "Marketplace",
  "HITL",
];

// IMPORTANTE: a tela de login usa hex literais (e nao tokens tematizados)
// porque queremos que ela seja SEMPRE escura, independente do tema do
// usuario. Os tokens ``--color-ink-950`` etc. invertem em dark mode, o
// que arruinava o contraste.
const BG_BASE = "#0a0710";
const CARD_BG = "rgba(22, 18, 30, 0.92)";
const CARD_BORDER = "rgba(255, 255, 255, 0.10)";

export function LoginPage() {
  const { t } = useTranslation("auth");
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [devUsers, setDevUsers] = useState<DevUser[] | null>(null);
  const [wordIdx, setWordIdx] = useState(0);

  // Navegacao reativa: assim que ``user`` for populado (via login() ou
  // hidratacao inicial do AuthProvider), redireciona para ``next``.
  useEffect(() => {
    if (user) {
      const next = searchParams.get("next") || "/";
      navigate(next, { replace: true });
    }
  }, [user, searchParams, navigate]);

  // Rotaciona a palavra do hero a cada 2.6s
  useEffect(() => {
    const id = window.setInterval(() => {
      setWordIdx((i) => (i + 1) % ROTATING_WORDS.length);
    }, 2600);
    return () => window.clearInterval(id);
  }, []);

  const showDevBypass = Boolean(env.DEV_BYPASS_USER_EMAIL);

  // Carrega catalogo de personas dev (2 times x 3 users + admin).
  // Endpoint responde 400 em prod (OIDC) -> ignoramos silenciosamente.
  useEffect(() => {
    if (!showDevBypass) return;
    let cancelled = false;
    api
      .GET<{ users: DevUser[] }>("/auth/dev/users")
      .then((r) => {
        if (!cancelled) setDevUsers(r.users);
      })
      .catch(() => {
        if (!cancelled) setDevUsers(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showDevBypass]);

  async function handleDevLogin(email?: string) {
    setLoading(true);
    try {
      // Token shape: "dev:<email>" -> backend parse_dev_token_email
      // resolve para a persona certa. Sem email = admin default.
      const token = email ? `dev:${email}` : "dev-bypass-token";
      await login(token);
    } catch (err) {
      toastApiError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="relative flex min-h-screen w-full overflow-hidden"
      style={{ backgroundColor: BG_BASE }}
    >
      {/* --- Background fullbleed: aurora blobs + grid + noise ---------- */}
      <div className="auth-grid-bg pointer-events-none absolute inset-0" />
      <div
        className="aurora-a pointer-events-none absolute -left-32 top-[-10%] h-[600px] w-[600px] rounded-full blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(214, 8, 82, 0.55) 0%, transparent 70%)",
        }}
      />
      <div
        className="aurora-b pointer-events-none absolute right-[-15%] top-[20%] h-[520px] w-[520px] rounded-full blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(255, 184, 0, 0.30) 0%, transparent 70%)",
        }}
      />
      <div
        className="aurora-c pointer-events-none absolute bottom-[-15%] left-[30%] h-[460px] w-[460px] rounded-full blur-[110px]"
        style={{
          background:
            "radial-gradient(circle, rgba(168, 85, 247, 0.25) 0%, transparent 70%)",
        }}
      />
      <div className="auth-noise pointer-events-none absolute inset-0 opacity-40" />

      {/* --- Hero (esquerda, hidden em mobile) -------------------------- */}
      <div className="relative z-10 hidden w-[54%] flex-col justify-between p-12 lg:flex">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] px-3 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-accent-400" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
              Plataforma Orbit
            </span>
          </span>
        </div>

        <div className="max-w-xl">
          <div
            className="hero-glow pointer-events-none absolute h-72 w-72 rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle, rgba(214, 8, 82, 0.55) 0%, transparent 70%)",
              left: "10%",
              top: "30%",
            }}
          />
          <h1 className="text-balance text-[44px] font-semibold leading-[1.05] tracking-[-0.02em] text-white sm:text-[56px]">
            Orquestre{" "}
            <span
              key={wordIdx}
              className="title-gradient inline-block"
              style={{ animation: "word-cycle-in 2.6s ease-in-out both" }}
            >
              {ROTATING_WORDS[wordIdx]}
            </span>
            <br />
            em um lugar só.
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/75">
            A camada de agentes do Sem Parar Empresas. Crie, treine,
            distribua e avalie bots conversacionais — com observabilidade
            ponta-a-ponta e governança fina por time.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3">
            <StatCard
              icon={<Layers className="h-4 w-4" />}
              label="Módulos"
              value="13"
            />
            <StatCard
              icon={<Target className="h-4 w-4" />}
              label="Cobertura"
              value="360°"
            />
            <StatCard
              icon={<Activity className="h-4 w-4" />}
              label="KPIs"
              value="60+"
            />
          </div>
        </div>

        {/* Marquee de keywords no rodape */}
        <div className="pointer-events-none overflow-hidden">
          <div className="marquee-track flex w-max gap-3">
            {[...MARQUEE_KEYWORDS, ...MARQUEE_KEYWORDS].map((word, i) => (
              <span
                key={`${word}-${i}`}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/15 bg-white/[0.05] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/70"
              >
                <Zap className="h-3 w-3 text-accent-400" />
                {word}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* --- Form (direita) -------------------------------------------- */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-8 lg:w-[46%] lg:px-12">
        <div
          className="auth-card-rise w-full max-w-md rounded-2xl p-8 shadow-2xl backdrop-blur"
          style={{
            backgroundColor: CARD_BG,
            border: `1px solid ${CARD_BORDER}`,
          }}
        >
          <div className="auth-stagger">
            <span className="inline-flex h-5 w-fit items-center gap-1 rounded-md bg-brand-500 px-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white">
              <Bot className="h-3 w-3" />
              Orbit
            </span>
            <h2 className="mt-3 text-balance text-[28px] font-semibold leading-[1.05] tracking-[-0.02em] text-white">
              {t("loginTitle")}
            </h2>
            <p className="mt-1 text-sm text-white/70">{t("loginSubtitle")}</p>

            <div className="mt-6 flex flex-col gap-2">
              <Button
                onClick={() => startOidcLogin()}
                disabled={loading}
                className="w-full"
              >
                <Wand2 className="h-4 w-4" />
                {t("signInWithGoogle")}
              </Button>
            </div>

            {showDevBypass ? (
              <>
                <div className="mt-6 flex items-center gap-2">
                  <span className="h-px flex-1 bg-white/15" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
                    Dev bypass
                  </span>
                  <span className="h-px flex-1 bg-white/15" />
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  {devUsers === null ? (
                    <DevPersonaButton
                      onClick={() => handleDevLogin()}
                      disabled={loading}
                      label="Admin (default)"
                      sublabel={env.DEV_BYPASS_USER_EMAIL ?? ""}
                      icon={
                        loading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Brain className="h-3.5 w-3.5" />
                        )
                      }
                    />
                  ) : (
                    devUsers.map((u) => (
                      <DevPersonaButton
                        key={u.email}
                        onClick={() => handleDevLogin(u.email)}
                        disabled={loading}
                        label={u.label}
                        sublabel={u.email}
                      />
                    ))
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-white/15 bg-white/[0.06] p-3 backdrop-blur">
      <div className="flex items-center gap-2 text-white/70">
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
          {label}
        </span>
      </div>
      <p className="tabular mt-1 text-2xl font-semibold text-white">{value}</p>
    </div>
  );
}

interface DevPersonaButtonProps {
  label: string;
  sublabel: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
}

/**
 * Botao customizado para personas dev. Layout vertical (label + email
 * embaixo), sempre escuro, sem dependencia do <Button> variantizado.
 * Garante contraste alto e largura uniforme.
 */
function DevPersonaButton({
  label,
  sublabel,
  onClick,
  disabled,
  icon,
}: DevPersonaButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group flex w-full items-center gap-3 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-left transition-colors hover:border-brand-400 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon ? (
        <span className="shrink-0 text-white/70 group-hover:text-white">
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-white">
          {label}
        </span>
        <span className="truncate text-[11px] font-mono text-white/55 group-hover:text-white/70">
          {sublabel}
        </span>
      </span>
    </button>
  );
}
